import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross1Icon } from "@radix-ui/react-icons";

/**
 * SimpleModal - the `isOpen`/`onClose` convenience API over the app's ONE
 * modal implementation (Radix Dialog, same as ui/Modal).
 *
 * This used to be a hand-rolled fixed-overlay div: no Esc-to-close, no focus
 * trap, no aria-modal, no body scroll lock. Rather than migrating every
 * caller to the compound ui/Modal API, the internals were swapped to Radix
 * under the SAME props - every consumer (Monitoring, Pilot Program, Staff
 * Access) got keyboard and screen-reader behavior for free.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Called when the modal should close (Esc, backdrop, X)
 * @param {string} title - Modal title
 * @param {React.ReactNode} children - Modal content
 */
const SIZE = { lg: "max-w-lg", xl: "max-w-2xl", "2xl": "max-w-3xl" };

export default function SimpleModal({ isOpen, onClose, title, children, size = "lg" }) {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={`fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-card shadow-xl max-h-[90vh] overflow-y-auto data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 ${SIZE[size] || SIZE.lg}`}
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <DialogPrimitive.Title className="text-xl font-semibold text-foreground">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="p-1 hover:bg-muted rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
              aria-label="Close"
            >
              <Cross1Icon className="w-5 h-5 text-muted-foreground" />
            </DialogPrimitive.Close>
          </div>
          <div className="p-6">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
